import { registerLocaleData } from '@angular/common';
import { LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import localeCo from '@angular/common/locales/es-CO';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BcButtonModule } from '@bancolombia/design-system-web/bc-button';
import { BcServicesModule } from '@bancolombia/design-system-web/bc-services';
import { CustomHttpInterceptorBearer } from './core/interceptors/custom/custom-interceptor';
import { TokenService } from './services/token-JWT/token.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtHelperService, JWT_OPTIONS } from '@auth0/angular-jwt';
import { CoreModule } from './core/core.module';
import { environment } from 'src/environments/environment';
import { BcIconModule } from '@bancolombia/design-system-web/bc-icon';
import { InFlightDedupInterceptor } from '@interceptor/inflight-dedup-interceptor';
import { firstValueFrom } from 'rxjs';

registerLocaleData(localeCo);
const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(message: any) {
  // This is intentional
}

export async function initializeMsal(): Promise<void> {
  inject(MsalBroadcastService);
  const msalService = inject(MsalService);

  try {
    const result = await firstValueFrom(msalService.handleRedirectObservable());

    const msal = msalService.instance;
    if (result?.account) {
      msal.setActiveAccount(result.account);
    } else if (!msal.getActiveAccount()) {
      const accounts = msal.getAllAccounts();
      if (accounts.length > 0) {
        msal.setActiveAccount(accounts[0]);
      }
    }
  } catch (e) {
    console.error('[MSAL] error en inicialización', e);
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      redirectUri: window.location.origin,
      authority: environment.authority,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    'user.read',
    'application.read.all',
  ]);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    AppRoutingModule,
    MsalModule,
    BcIconModule.forRoot({
      path: 'https://library-sdb.apps.bancolombia.com/bds/8.36.5',
    }),
    BcServicesModule.forRoot(),
    BcButtonModule,
    CoreModule.forRoot(),
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'es-CO' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: InFlightDedupInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHttpInterceptorBearer,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    provideAppInitializer(initializeMsal),
    {
      provide: JWT_OPTIONS,
      useValue: JWT_OPTIONS,
    },
    JwtHelperService,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    TokenService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
